import React, { useRef, useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Form, Input, Button, message, Divider, Tooltip, Spin } from 'antd';
import {
  UserOutlined, MailOutlined, PhoneOutlined, LockOutlined,
  SaveOutlined, EditOutlined, CloseOutlined, IdcardOutlined,
  BankOutlined, CameraOutlined,
} from '@ant-design/icons';
import { RootState } from '../../redux/store';
import { MobileDrawer } from '../../components/ui';
import { update } from '../../redux/slice/userSlice';
import { GET, PATCH, POST, PUT, UPLOAD } from '../../utils/apiRequest';
import API from '../../config/apis';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:3001';
const toAbsUrl = (path: string | null | undefined) =>
  !path ? null : path.startsWith('http') ? path : `${API_BASE}${path}`;

const ROLE_LABELS: Record<string, string> = {
  SUPER_ADMIN: 'Super Admin',
  INSTITUTION_ADMIN: 'Institution Admin',
  FEST_ADMIN: 'Fest Admin',
  ITEM_COORDINATOR: 'Item Coordinator',
  JUDGE: 'Judge',
  VOLUNTEER: 'Volunteer',
  PARTICIPANT: 'Participant',
};

const FieldRow: React.FC<{ icon: React.ReactNode; label: string; value?: string; muted?: boolean; note?: string }> = ({
  icon, label, value, muted, note,
}) => (
  <div>
    <div style={{ fontSize: 11, fontWeight: 500, color: 'var(--muted-foreground)', marginBottom: 5, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
      {label}
    </div>
    <div style={{
      display: 'flex', alignItems: 'center', gap: 10,
      padding: '8px 12px', borderRadius: 8,
      border: '1px solid var(--border)', background: 'var(--muted)', minHeight: 36,
    }}>
      <span style={{ color: 'var(--muted-foreground)', fontSize: 13, flexShrink: 0 }}>{icon}</span>
      <span style={{ fontSize: 13, color: muted ? 'var(--muted-foreground)' : 'var(--foreground)', flex: 1 }}>
        {value || <span style={{ color: 'var(--muted-foreground)', fontStyle: 'italic' }}>—</span>}
      </span>
    </div>
    {note && <div style={{ fontSize: 11, color: 'var(--muted-foreground)', marginTop: 3 }}>{note}</div>}
  </div>
);

interface ProfileDrawerProps {
  open: boolean;
  onClose: () => void;
}

const ProfileDrawer: React.FC<ProfileDrawerProps> = ({ open, onClose }) => {
  const dispatch = useDispatch();
  const { user } = useSelector((state: RootState) => state.User);

  const [profile, setProfile] = useState<any>(null);
  const [saving, setSaving] = useState(false);
  const [changingPwd, setChangingPwd] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [institutionId, setInstitutionId] = useState<string | null>(null);
  const [institutionLogo, setInstitutionLogo] = useState<string | null>(null);
  const [logoUploading, setLogoUploading] = useState(false);
  const [photoUploading, setPhotoUploading] = useState(false);
  const logoInputRef = useRef<HTMLInputElement>(null);
  const photoInputRef = useRef<HTMLInputElement>(null);

  const [profileForm] = Form.useForm();
  const [pwdForm] = Form.useForm();

  const isInstitutionAdmin = (user?.role || '') === 'INSTITUTION_ADMIN';

  useEffect(() => {
    if (open) {
      fetchProfile();
      if (isInstitutionAdmin) fetchInstitution();
    }
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchProfile = async () => {
    const res = await GET(API.PROFILE_ME);
    if (res.status && res.data) setProfile(res.data);
  };

  const fetchInstitution = async () => {
    const instId = (user as any)?.institution_id;
    if (!instId) return;
    setInstitutionId(instId);
    const res = await GET(`${API.INSTITUTIONS}/${instId}`);
    if (res.status && res.data) {
      const inst = (res.data as any).data || res.data;
      if (inst.logo) setInstitutionLogo(inst.logo);
    }
  };

  const handleLogoChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !institutionId) return;
    setLogoUploading(true);
    const fd = new FormData();
    fd.append('file', file);
    const uploadRes = await UPLOAD(API.UPLOAD_LOGO, fd);
    if (uploadRes.status && uploadRes.data) {
      const url = (uploadRes.data as any).data?.url || (uploadRes.data as any).url;
      const saveRes = await PUT(`${API.INSTITUTIONS}/${institutionId}`, { logo: url });
      if (saveRes.status) { setInstitutionLogo(url); message.success('School logo updated'); }
      else message.error((saveRes as any).message || 'Failed to save logo');
    } else {
      message.error((uploadRes as any).message || 'Logo upload failed');
    }
    setLogoUploading(false);
    e.target.value = '';
  };

  const handlePhotoChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setPhotoUploading(true);
    const fd = new FormData();
    fd.append('file', file);
    const res = await UPLOAD(API.UPLOAD_PROFILE_PHOTO, fd);
    if (res.status && res.data) {
      const url = (res.data as any).data?.url || (res.data as any).url;
      const saveRes = await PATCH(API.PROFILE_ME, { photo: url });
      if (saveRes.status) {
        setProfile((prev: any) => prev ? { ...prev, photo: url } : prev);
        dispatch(update({ photo: url }));
        message.success('Profile photo updated');
      } else {
        message.error((saveRes as any).message || 'Failed to save photo');
      }
    } else {
      message.error((res as any).message || 'Photo upload failed');
    }
    setPhotoUploading(false);
    e.target.value = '';
  };

  const openEdit = () => {
    profileForm.setFieldsValue({ name: profile?.name || user?.name || '', phone: profile?.phone || '', bio: profile?.bio || '' });
    setEditMode(true);
  };

  const cancelEdit = () => { setEditMode(false); profileForm.resetFields(); };

  const handleSaveProfile = async (values: { name: string; phone?: string; bio?: string }) => {
    setSaving(true);
    const res = await PATCH(API.PROFILE_ME, {
      name: values.name,
      ...(values.phone !== undefined ? { phone: values.phone } : {}),
      ...(values.bio !== undefined ? { bio: values.bio } : {}),
    });
    if (res.status) {
      message.success('Profile updated successfully');
      dispatch(update({ name: values.name }));
      setProfile((prev: any) => prev ? { ...prev, name: values.name, phone: values.phone, bio: values.bio } : prev);
      setEditMode(false);
    } else {
      message.error(res.message || 'Failed to update profile');
    }
    setSaving(false);
  };

  const handleChangePassword = async (values: { oldPassword: string; newPassword: string; confirmPassword: string }) => {
    if (values.newPassword !== values.confirmPassword) { message.error('New passwords do not match'); return; }
    setChangingPwd(true);
    const res = await POST(API.CHANGE_PASSWORD, { oldPassword: values.oldPassword, newPassword: values.newPassword });
    if (res.status) { message.success('Password changed successfully'); pwdForm.resetFields(); }
    else message.error(res.message || 'Failed to change password');
    setChangingPwd(false);
  };

  const displayName   = profile?.name   || user?.name   || '';
  const displayEmail  = profile?.email  || user?.email  || '';
  const displayRole   = profile?.role   || user?.role   || '';
  const displayStatus = profile?.status || 'ACTIVE';
  const initials = displayName.split(' ').filter(Boolean).map((w: string) => w[0]).join('').toUpperCase().slice(0, 2) || 'U';

  return (
    <MobileDrawer
      title="My Profile"
      open={open}
      onClose={() => { setEditMode(false); profileForm.resetFields(); onClose(); }}
      width={480}
      destroyOnHidden={false}
      styles={{ body: { padding: '20px 24px', paddingBottom: 80 } }}
    >
      {/* Avatar row */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginBottom: 20 }}>
        {isInstitutionAdmin ? (
          <div style={{ position: 'relative', flexShrink: 0 }}>
            <div style={{
              width: 60, height: 60, borderRadius: 12,
              background: institutionLogo ? 'transparent' : 'var(--muted)',
              border: '2px solid var(--border)',
              display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden',
            }}>
              {institutionLogo
                ? <img src={toAbsUrl(institutionLogo)!} alt="Logo" style={{ width: '100%', height: '100%', objectFit: 'contain', padding: 4 }} />
                : <BankOutlined style={{ fontSize: 24, color: 'var(--muted-foreground)' }} />}
            </div>
            <Tooltip title="Change school logo">
              <button type="button" onClick={() => logoInputRef.current?.click()} disabled={logoUploading} style={{
                position: 'absolute', bottom: -5, right: -5,
                width: 20, height: 20, borderRadius: '50%',
                background: 'var(--primary)', color: '#fff',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                border: '2px solid var(--background)', cursor: 'pointer', padding: 0,
                opacity: logoUploading ? 0.6 : 1,
              }}>
                <CameraOutlined style={{ fontSize: 10 }} />
              </button>
            </Tooltip>
            <input ref={logoInputRef} type="file" accept="image/*" style={{ display: 'none' }} onChange={handleLogoChange} />
          </div>
        ) : (
          <div style={{ position: 'relative', flexShrink: 0 }}>
            <div style={{
              width: 60, height: 60, borderRadius: '50%',
              background: profile?.photo ? 'transparent' : 'var(--primary)',
              color: 'var(--primary-foreground)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontSize: 22, fontWeight: 700,
              overflow: 'hidden', border: profile?.photo ? '2px solid var(--border)' : 'none',
            }}>
              {profile?.photo
                ? <img src={profile.photo} alt="Profile" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                : initials}
            </div>
            <Tooltip title="Change profile photo">
              <button type="button" onClick={() => photoInputRef.current?.click()} disabled={photoUploading} style={{
                position: 'absolute', bottom: -3, right: -3,
                width: 20, height: 20, borderRadius: '50%',
                background: 'var(--primary)', color: '#fff',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                border: '2px solid var(--background)', cursor: 'pointer', padding: 0,
                opacity: photoUploading ? 0.6 : 1,
              }}>
                {photoUploading ? <Spin size="small" /> : <CameraOutlined style={{ fontSize: 10 }} />}
              </button>
            </Tooltip>
            <input ref={photoInputRef} type="file" accept="image/*" style={{ display: 'none' }} onChange={handlePhotoChange} />
          </div>
        )}

        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 17, fontWeight: 700, color: 'var(--foreground)', lineHeight: 1.3 }}>{displayName || '—'}</div>
          <div style={{ fontSize: 12, color: 'var(--muted-foreground)', marginTop: 2 }}>{ROLE_LABELS[displayRole] || displayRole}</div>
          <span style={{
            display: 'inline-flex', alignItems: 'center', gap: 4, marginTop: 5,
            fontSize: 11, fontWeight: 500, padding: '2px 8px', borderRadius: 20,
            background: displayStatus === 'ACTIVE' ? 'color-mix(in srgb, #22c55e 14%, transparent)' : 'color-mix(in srgb, #ef4444 14%, transparent)',
            color: displayStatus === 'ACTIVE' ? '#16a34a' : '#dc2626',
          }}>
            <span style={{ width: 5, height: 5, borderRadius: '50%', background: displayStatus === 'ACTIVE' ? '#22c55e' : '#ef4444', display: 'inline-block' }} />
            {displayStatus}
          </span>
        </div>

        {!editMode && (
          <Button icon={<EditOutlined />} size="small" onClick={openEdit}>Edit</Button>
        )}
      </div>

      <Divider style={{ margin: '0 0 20px', borderColor: 'var(--border)' }} />

      {/* View mode */}
      {!editMode && (
        <div className="space-y-4">
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '14px 20px' }}>
            <FieldRow icon={<UserOutlined />}   label="Full Name"     value={displayName} />
            <FieldRow icon={<MailOutlined />}   label="Email"         value={displayEmail} muted note="Cannot be changed" />
            <FieldRow icon={<PhoneOutlined />}  label="Phone"         value={profile?.phone} />
            <FieldRow icon={<IdcardOutlined />} label="Role"          value={ROLE_LABELS[displayRole] || displayRole} muted />
          </div>
          <div>
            <div style={{ fontSize: 11, fontWeight: 500, color: 'var(--muted-foreground)', marginBottom: 5, textTransform: 'uppercase', letterSpacing: '0.04em' }}>Bio</div>
            <div style={{
              padding: '9px 12px', borderRadius: 8,
              border: '1px solid var(--border)', background: 'var(--muted)',
              fontSize: 13, color: profile?.bio ? 'var(--foreground)' : 'var(--muted-foreground)',
              fontStyle: profile?.bio ? 'normal' : 'italic', minHeight: 60, lineHeight: 1.6,
            }}>
              {profile?.bio || '—'}
            </div>
          </div>
        </div>
      )}

      {/* Edit mode */}
      {editMode && (
        <Form form={profileForm} layout="vertical" onFinish={handleSaveProfile}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0 16px' }}>
            <Form.Item label="Full Name" name="name" rules={[{ required: true, message: 'Name is required' }]}>
              <Input prefix={<UserOutlined />} placeholder="Your full name" />
            </Form.Item>
            <Form.Item label="Email">
              <Input prefix={<MailOutlined />} value={displayEmail} disabled />
              <div style={{ fontSize: 11, color: 'var(--muted-foreground)', marginTop: 3 }}>Email cannot be changed</div>
            </Form.Item>
            <Form.Item label="Phone" name="phone">
              <Input prefix={<PhoneOutlined />} placeholder="+91 98765 43210" />
            </Form.Item>
            <Form.Item label="Role">
              <Input prefix={<IdcardOutlined />} value={ROLE_LABELS[displayRole] || displayRole} disabled />
            </Form.Item>
          </div>
          <Form.Item label="Bio" name="bio">
            <Input.TextArea placeholder="Tell something about yourself..." rows={3} style={{ resize: 'none' }} />
          </Form.Item>
          <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
            <Button icon={<CloseOutlined />} onClick={cancelEdit}>Cancel</Button>
            <Button type="primary" htmlType="submit" icon={<SaveOutlined />} loading={saving}>Save Changes</Button>
          </div>
        </Form>
      )}

      <Divider style={{ margin: '24px 0', borderColor: 'var(--border)' }} />

      {/* Change Password */}
      <div style={{ marginBottom: 16 }}>
        <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--foreground)', marginBottom: 2 }}>Change Password</div>
        <div style={{ fontSize: 12, color: 'var(--muted-foreground)' }}>Update your password to keep your account secure</div>
      </div>
      <Form form={pwdForm} layout="vertical" onFinish={handleChangePassword}>
        <Form.Item label="Current Password" name="oldPassword" rules={[{ required: true, message: 'Required' }]}>
          <Input.Password prefix={<LockOutlined />} placeholder="Current password" />
        </Form.Item>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0 16px' }}>
          <Form.Item label="New Password" name="newPassword" rules={[{ required: true, message: 'Required' }, { min: 8, message: 'Min 8 characters' }, { pattern: /^(?=.*[A-Z])/, message: 'Must include at least 1 uppercase letter' }]}>
            <Input.Password prefix={<LockOutlined />} placeholder="New password" />
          </Form.Item>
          <Form.Item label="Confirm Password" name="confirmPassword" rules={[{ required: true, message: 'Required' }]}>
            <Input.Password prefix={<LockOutlined />} placeholder="Confirm password" />
          </Form.Item>
        </div>
        <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
          <Button type="primary" htmlType="submit" icon={<LockOutlined />} loading={changingPwd}>Change Password</Button>
        </div>
      </Form>

      {profile?.createdAt && (
        <div style={{ marginTop: 20, fontSize: 11, color: 'var(--muted-foreground)' }}>
          Member since {new Date(profile.createdAt).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}
        </div>
      )}
    </MobileDrawer>
  );
};

export default ProfileDrawer;
